/**
 * DataGapReportsTile — small counter tile for the Admin Progress
 * Dash. Pings /api/admin/data-gap-reports/summary once on mount
 * (and every 60s) to show ops if the "help us improve" inbox has
 * new items. Links straight to the full triage page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const REFRESH_MS = 60_000;

export default function DataGapReportsTile() {
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(
          `/admin/data-gap-reports/summary`,
        );
        if (!cancelled) setSummary(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const newCount = summary?.counts?.new ?? 0;
  const hasNew = newCount > 0;

  return (
    <Link
      to="/admin/data-gap-reports"
      data-testid="tile-data-gap-reports"
      className="block border border-stone-200 rounded-xl p-4 bg-white hover:border-purple-300 hover:shadow-sm transition-all group"
    >
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${
            hasNew ? "bg-purple-100 text-purple-800" : "bg-stone-100 text-stone-500"
          }`}
        >
          <MessageSquare className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-stone-900">
              Family feedback inbox
            </h3>
            {hasNew && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-600 text-white font-medium"
                data-testid="tile-data-gap-reports-new-badge"
              >
                {newCount} new
              </span>
            )}
          </div>
          <p className="mt-0.5 text-[12px] text-stone-500 leading-snug">
            Empty-cell reports from course pages — hints, sources, or
            corrections families flagged for us to ingest.
          </p>
          {err ? (
            <p className="mt-2 text-[11px] text-rose-700">{err}</p>
          ) : summary ? (
            <div className="mt-2 flex gap-3 text-[11px] font-mono text-stone-600">
              <span>
                <span className="text-stone-400">new</span> {summary.counts?.new ?? 0}
              </span>
              <span>
                <span className="text-stone-400">reviewed</span> {summary.counts?.reviewed ?? 0}
              </span>
              <span>
                <span className="text-stone-400">actioned</span> {summary.counts?.actioned ?? 0}
              </span>
              <span>
                <span className="text-stone-400">dismissed</span> {summary.counts?.dismissed ?? 0}
              </span>
            </div>
          ) : (
            <div className="mt-2 h-3 w-32 bg-stone-100 animate-pulse rounded" />
          )}
        </div>
        <ArrowRight className="h-4 w-4 text-stone-400 group-hover:text-purple-700 mt-1" />
      </div>
    </Link>
  );
}
